import React, { useState, useCallback, useRef } from "react";
import { Box, Typography, Paper, Stack, CircularProgress } from "@mui/material";
import { LocationOn } from "@mui/icons-material";

import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
//@ts-ignore
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { Dialog } from "../Dialogs/Dialog";

export interface IAddressDetails {
  address: string;
  city: string;
  state: string;
  area: string;
  pin_code: string;
  latitude: number;
  longitude: number;
}

interface IMapPickerProps {
  open: boolean;
  onClose: () => void;
  onHandleLocationSelect: (place: any, addressDetails?: IAddressDetails) => void;
}

interface ISelectedLocation {
  latitude: number;
  longitude: number;
  name: string;
  address: string;
}

/** Parse Google Geocoder address_components into city, state, area, pin_code */
function parseAddressComponents(
  addressComponents: google.maps.GeocoderAddressComponent[],
  formattedAddress: string,
  latitude: number,
  longitude: number
): IAddressDetails {
  let city = "";
  let state = "";
  let area = "";
  let pin_code = "";
  let district = "";
  let locality = "";
  let sublocalities: string[] = [];

  for (const component of addressComponents) {
    const types = component.types;
    if (types.includes("locality")) {
      locality = component.long_name;
    } else if (types.includes("administrative_area_level_1")) {
      state = component.long_name;
    } else if (types.includes("postal_code")) {
      pin_code = component.long_name;
    } else if (
      types.includes("sublocality") ||
      types.includes("sublocality_level_1") ||
      types.includes("sublocality_level_2") ||
      types.includes("neighborhood")
    ) {
      sublocalities.push(component.long_name);
    } else if (types.includes("administrative_area_level_2")) {
      district = component.long_name;
    }
  }

  const isDivision = (s: string) => /division/i.test(s);

  // RSA logic for City and Area:
  // 1. If we have sublocalities (e.g., "Sector 18"), they are the Area, and Town/Locality is the City.
  // 2. If we don't have sublocalities (short address), we try to use District as City and Town/Locality as Area.
  // 3. We avoid names containing "Division" for City if possible.

  if (sublocalities.length > 0) {
    area = sublocalities.join(", ");
    city = locality || district || "";
    // If city is a division, try to use locality instead
    if (isDivision(city) && locality && !isDivision(locality)) {
      city = locality;
    }
  } else {
    // Short address fallback
    area = locality || "";
    city = locality || "";

    if (district && !isDivision(district)) {
      if (locality && locality !== district) {
        city = district;
        area = locality;
      } else {
        city = district;
        area = district; // Fallback to district if locality is missing
      }
    }
  }

  return {
    address: formattedAddress || "",
    city: city || "",
    state,
    area: area || "",
    pin_code,
    latitude,
    longitude,
  };
}

const containerStyle = {
  width: "100%",
  height: "400px",
};

const libraries: ("places" | "geometry")[] = ["places"];

export const MapPicker = (props: IMapPickerProps) => {
  const { open, onClose, onHandleLocationSelect } = props;
  const [selectedLocation, setSelectedLocation] =
    useState<ISelectedLocation | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [selectedAddressDetails, setSelectedAddressDetails] =
    useState<IAddressDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 18.5204, lng: 73.8567 }); // Default to Pune
  const [mapZoom, setMapZoom] = useState(12);
  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const mapRef = useRef<google.maps.Map | null>(null);

  const apiKey =
    process.env.REACT_APP_GOOGLE_MAP_API_KEY ||
    process.env.REACT_APP_GOOGLE_MAP_KEY ||
    "";

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const handlePlaceChange = async (place: any) => {
    // Handle case when user clears the selection
    if (!place || !place.value) {
      setSelectedLocation(null);
      setSelectedPlace(null);
      setSelectedAddressDetails(null);
      return;
    }

    const placeId = place.value.place_id;
    if (!placeId) return;

    setIsLoading(true);
    setSelectedPlace(place);

    try {
      // Get the detailed place information using the placeId (same as code 2)
      const response = await geocodeByPlaceId(placeId);
      const { geometry, address_components, formatted_address } = response[0];
      const { lat, lng } = geometry.location;

      // Extract address components
      let address = formatted_address || "";
      let name = "";

      let establishmentName = "";
      let sublocalityName = "";
      let localityName = "";
      let routeName = "";

      // Try to get a meaningful name by priority: Store/Place > Area > Town > Street
      for (const component of address_components) {
        const types = component.types;
        if (types.includes("establishment")) establishmentName = component.long_name;
        if (types.includes("sublocality") || types.includes("sublocality_level_1")) sublocalityName = component.long_name;
        if (types.includes("locality")) localityName = component.long_name;
        if (types.includes("route")) routeName = component.long_name;
      }

      name = establishmentName || sublocalityName || localityName || routeName || "";

      // If no name found, use the first part of the address
      if (!name && address) {
        name = address.split(",")[0];
      }

      const locationData = {
        latitude: lat(),
        longitude: lng(),
        name: name || "Selected Location",
        address: address,
      };

      const addressDetails = parseAddressComponents(
        address_components,
        address,
        lat(),
        lng()
      );
      setSelectedAddressDetails(addressDetails);
      setSelectedLocation(locationData);
      setMarker({ lat: lat(), lng: lng() });
      setMapCenter({ lat: lat(), lng: lng() });
      setMapZoom(15);

      // Center map on selected location
      if (mapRef.current) {
        mapRef.current.setCenter({ lat: lat(), lng: lng() });
        mapRef.current.setZoom(15);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching location data:", error);
      setIsLoading(false);
      setSelectedLocation(null);
      setSelectedPlace(null);
      setSelectedAddressDetails(null);
    }
  };

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({ lat, lng });
    setIsLoading(true);

    try {
      // Reverse Geocode to get address
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat, lng } },
        (results: google.maps.GeocoderResult[] | null, status) => {
          setIsLoading(false);
          if (status === "OK" && results && results[0]) {
            const address = results[0].formatted_address;
            let name = "Selected Location";

            // Try to get a meaningful name from address components
            const addressComponents = results[0].address_components;
            let establishmentName = "";
            let sublocalityName = "";
            let localityName = "";
            let routeName = "";

            // Try to get a meaningful name from address components by priority
            if (addressComponents) {
              for (const component of addressComponents) {
                const types = component.types;
                if (types.includes("establishment")) establishmentName = component.long_name;
                if (types.includes("sublocality") || types.includes("sublocality_level_1")) sublocalityName = component.long_name;
                if (types.includes("locality")) localityName = component.long_name;
                if (types.includes("route")) routeName = component.long_name;
              }
            }
            
            name = establishmentName || sublocalityName || localityName || routeName || "Selected Location";

            // If no name found, use first part of address
            if (name === "Selected Location" && address) {
              name = address.split(",")[0];
            }

            const locationData = {
              latitude: lat,
              longitude: lng,
              address: address,
              name: name,
            };

            const addressDetails = parseAddressComponents(
              addressComponents || [],
              address || "",
              lat,
              lng
            );
            setSelectedAddressDetails(addressDetails);
            setSelectedLocation(locationData);

            // Create a place object for map clicks using the place_id from geocoding result
            // This allows handleLocationSelect to work properly with geocodeByPlaceId
            const placeFromMap = {
              value: {
                place_id: results[0].place_id,
                description: address,
              },
              label: name,
            };
            setSelectedPlace(placeFromMap);
          } else {
            // If geocoding fails, still set the location with coordinates
            const locationData = {
              latitude: lat,
              longitude: lng,
              address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              name: "Selected Location",
            };
            setSelectedAddressDetails({
              address: locationData.address,
              city: "",
              state: "",
              area: "",
              pin_code: "",
              latitude: lat,
              longitude: lng,
            });
            setSelectedLocation(locationData);

            // If geocoding fails, we need to create coordinates-based place object
            // We'll use the coordinates directly and handleLocationSelect will need to handle this
            // For now, we'll create a minimal place object - but this might need special handling
            const placeFromMap = {
              value: {
                place_id: null, // No place_id available
                description: locationData.address,
                geometry: {
                  location: {
                    lat: () => lat,
                    lng: () => lng,
                  },
                },
                formatted_address: locationData.address,
                address_components: [],
              },
              label: "Selected Location",
            };
            setSelectedPlace(placeFromMap);
          }
        }
      );
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setIsLoading(false);
    }
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleSendLocation = () => {
    if (selectedPlace) {
      onHandleLocationSelect(selectedPlace, selectedAddressDetails ?? undefined);
      // Reset state after sending
      setSelectedLocation(null);
      setSelectedPlace(null);
      setSelectedAddressDetails(null);
    }
  };

  const handleClose = () => {
    // Reset state on close
    setSelectedLocation(null);
    setSelectedPlace(null);
    setSelectedAddressDetails(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      title="Select Location"
      onClose={handleClose}
      size="lg"
      actions={[
        {
          type: "button",
          label: "Cancel",
          variant: "text",
          onClick: handleClose,
        },
        {
          type: "button",
          label: "Send Location",
          variant: "contained",
          onClick: handleSendLocation,
          disabled: !selectedLocation,
        },
      ]}
    >
      <Box>
        {!isLoaded ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading...
            </Typography>
          </Box>
        ) : !apiKey ? (
          <Typography variant="body2" color="error" sx={{ p: 2 }}>
            Google Maps API key is missing. Set REACT_APP_GOOGLE_MAP_API_KEY in
            your .env file and restart the dev server.
          </Typography>
        ) : (
          <GooglePlacesAutocomplete
            selectProps={{
              placeholder: "Search for a location...",
              isClearable: true,
              onChange: handlePlaceChange,
            }}
            autocompletionRequest={{
              componentRestrictions: {
                country: ["in"],
              },
            }}
          />
        )}

        {isLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 2,
              p: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading location details...
            </Typography>
          </Box>
        )}

        {selectedLocation && !isLoading && (
          <Paper
            elevation={2}
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOn color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  {selectedLocation.name}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                {selectedLocation.address}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexWrap: "wrap",
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Latitude
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedLocation.latitude.toFixed(6)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Longitude
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedLocation.longitude.toFixed(6)}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        )}

        {!selectedLocation && !isLoading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, fontSize: "0.75rem" }}
          >
            Search or click on the map to select a location
          </Typography>
        )}

        {/* Google Map */}
        <Box
          sx={{
            mt: 3,
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {!isLoaded ? (
            <Box
              sx={{
                textAlign: "center",
                p: 3,
                height: containerStyle.height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading map...
              </Typography>
            </Box>
          ) : (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapCenter}
              zoom={mapZoom}
              onClick={handleMapClick}
              onLoad={onMapLoad}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
              }}
            >
              {marker && <Marker position={marker} />}
            </GoogleMap>
          )}

          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                bgcolor: "background.paper",
                p: 1,
                borderRadius: 1,
                boxShadow: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="caption">Loading...</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};